import projectsData from '~/data/projects.json'

export type ProjectType = 'portfolio' | 'pet-project'

export interface ProjectImage {
  src: string
  alt: string
  fit?: 'contain'
}

export interface Project {
  slug: string
  legacyId?: string | null
  name: string
  subtitle: string
  url?: string | null
  type: ProjectType
  date: string
  aboutClient?: string | null
  objective?: string | null
  tools?: string | null
  content: string
  stack?: string[]
  links?: { appStore?: string, googlePlay?: string }
  images: ProjectImage[]
}

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  'portfolio': 'Client Work',
  'pet-project': 'Own Project',
}

const imageUrls = import.meta.glob<string>('~/assets/images/projects/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
})

function imageUrl (file: string) {
  const match = Object.entries(imageUrls).find(([path]) => path.endsWith(`/${file}`))
  if (!match) { throw new Error(`Project image not found: ${file}`) }
  return match[1]
}

export const projects: Project[] = (projectsData as unknown as Project[])
  .map(project => ({
    ...project,
    images: project.images.map(image => ({ ...image, src: imageUrl(image.src) })),
  }))
  .sort((a, b) => b.date.localeCompare(a.date))

export const apps = projects.filter(project => project.links?.appStore || project.links?.googlePlay)

export function findProject (slug: string) {
  return projects.find(project => project.slug === slug)
}

export function relatedProjects (project: Project, limit = 4) {
  return projects
    .filter(other => other.slug !== project.slug && other.type === project.type)
    .slice(0, limit)
}

export function formatDate (value: string, options: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' }) {
  return new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC', ...options })
}

export function projectsForService (slug: string, limit = 3) {
  const list = slug === 'mobile-app-development' ? apps : projects.filter(project => project.type === 'portfolio')
  return list.slice(0, limit)
}
